import dayjs from "dayjs";
import { firestore } from "@/firebaseConfig";
import {
    getTimeEntriesForUsersInRange,
    shouldFetchFromFirestore,
    getFirestoreDateRange,
    getDateRangeForPeriod,
} from "@/utils/firestoreUtils";
import {
    normalizeDateRange,
    formatTimeFromHours,
    formatTimeFromMs,
} from "@/utils/timeFormatUtils";

export {
    normalizeDateRange,
    formatTimeFromHours,
    formatTimeFromMs,
};

/**
 * Normalizes time entries to an array format.
 * Handles both array and object (map) formats.
 *
 * @param {Array|Object} entries - Time entries as array or object map
 * @returns {Array} Normalized array of entries
 */
export function normalizeTimeEntries(entries) {
    if (Array.isArray(entries)) return entries;
    if (typeof entries === "object" && entries !== null) {
        return Object.values(entries);
    }
    return [];
}

/**
 * Build a lookup map from projectId to project name.
 *
 * Iterates over the `projects` array (if present), pulling out each
 * `projectId` and `name`, and returns an object where keys are
 * project IDs and values are the corresponding project names.
 *
 * @name projectMap
 * @returns {Object.<string, string>} An object mapping each `projectId` to its `name`.
 */
export function projectMap(projects) {
    return (projects || []).reduce((m, p) => {
        m[p.projectId] = p.name;
        return m;
    }, {});
}

/**
 * Returns the open (no endTime) time entry for a user, or null.
 * The object includes the entry uid: { uid, ...entry }.
 */
export function getActiveEntry(userObject) {
    const entriesObject = userObject?.timeEntries;
    if (!entriesObject) return null;
  
    let latestOpenEntry = null;
    let latestOpenStartTime = 0; // server timestamps are positive numbers
  
    for (const [entryId, entryData] of Object.entries(entriesObject)) {
      // Only consider entries that are still open (no endTime)
      if (!entryData || entryData.endTime) continue;
  
      const entryStartTime = entryData.startTime; // guaranteed number from RTDB
      if (entryStartTime > latestOpenStartTime) {
        latestOpenStartTime = entryStartTime;
        latestOpenEntry = { uid: entryId, ...entryData };
      }
    }
  
    return latestOpenEntry; // null if no open entry
  }

/**
 * Build a lookup map from projectId to project priority.
 *
 * Iterates over the `projects` array (if present), pulling out each
 * `projectId` and `priority`. If a project has no explicit priority,
 * defaults to `"Normal"`. Returns an object where keys are project IDs
 * and values are the corresponding priority strings.
 *
 * @name priorityMap
 * @returns {Object.<string, string>} An object mapping each `projectId` to its priority.
 */
export function priorityMap(projects) {
    return (projects || []).reduce((m, p) => {
        m[p.projectId] = p.priority || "Normal";
        return m;
    }, {});
}

/**
 * Given a flat list of time‐entry objects
 * (each with projectId, description, startTime, endTime),
 * group them into a map: projectId → [ "Desc (1h:15min)", … ].
 *
 * @param {Array<Object>} entries
 * @returns {Record<string,string[]>}
 */
export function groupDescriptionsByProject(entries) {
    return entries.reduce(
        (acc, { projectId, description, startTime, endTime }) => {
            if (!description || !endTime) return acc;
            const mins = (endTime - startTime) / 60000;
            const h = Math.floor(mins / 60);
            const m = Math.round(mins % 60);
            const label = h ? `${h}h:${m}min` : `${m}min`;
            (acc[projectId] ||= []).push(`${description} (${label})`);
            return acc;
        },
        {}
    );
}

/**
 * Filters a map of time-entry objects to only those whose startTime
 * falls within the given date or date range.
 * Accepts either Date objects or UTC epoch milliseconds (numbers).
 *
 * @param {Date | number | Array<Date|number>} dateInput
 *   - Single Date or number (ms) → filter that day
 *   - [ startDate, endDate ] → filter inclusive range
 * @param {Object.<string, { startTime: number|string, [key: string]: any }>} entries
 *   Map of entryId → entry object (must have `startTime` in UTC milliseconds)
 * @returns {Array.<{ id: string, startTime: number, [key: string]: any }>}
 */


export function filterTimeEntriesForDate(dateInput, entries) {
    if (!entries) {
      console.warn("filterTimeEntriesForDate - entries are null or undefined");
      return [];
    }
  
    let startUtc, endUtc;
  
    if (Array.isArray(dateInput)) {
      if (dateInput.length === 0) return [];
      const start = dateInput[0];
      const end   = dateInput[dateInput.length - 1];
  
      const normalized = normalizeDateRange(start, end);
      if (!normalized) return [];
      startUtc = normalized.start;
      endUtc = normalized.end;
    } else {
      const normalized = normalizeDateRange(dateInput, dateInput);
      if (!normalized) return [];
      startUtc = normalized.start;
      endUtc = normalized.end;
    }
  
    return Object.entries(entries)
      .filter(([, entry]) => {
        const entryStart = Number(entry.startTime);
        return entryStart >= startUtc && entryStart <= endUtc;
      })
      .map(([id, entry]) => ({
        id,
        ...entry,
      }));
  }

/**
 * Merge a per‐project summary array with a descriptions map
 * (as produced by groupDescriptionsByProject).
 *
 * @param {Array<Object>} summary
 * @param {Record<string,string[]>} descMap
 * @returns {Array<Object>}
 */
export function extendSummaryWithDescriptions(summary, descMap) {
    return summary.map((proj) => ({
        ...proj,
        description: descMap[proj.id] || [],
    }));
}

/**
 * Calculates the total time worked for each project on a specific date.
 *
 * It filters the user's time entries to include only those matching the selected date,
 * calculates the total time worked for each project, and formats the results for display.
 *
 * @param {Object} user - The user object containing time entries.
 * @param {Array<Object>} projects - An array of project objects, each containing projectId and name.
 * @param {Object} options - Options object
 * @param {Date} options.date - Single date to filter by
 * @param {Object} options.dateRange - Date range object with startDate and endDate
 * @param {boolean} options.excludeEmpty - Whether to exclude projects with no entries
 * @param {Object} options.mergedTimeEntries - Optional merged time entries (RTDB + Firestore). If provided, uses this instead of user.timeEntries
 * @returns {Array<Object>} An array of objects, each containing:
 *   - `projectName` {string}: The name of the project (or "Unknown" if the project ID is not found).
 *   - `totalTimeWorked` {string}: The total time worked on the project for the selected date, formatted as "Xh:Ymin".
 */
export function calculateProjectsWorkSummaryForUser(user, projects, options = {}) {
    if (!user || !projects) {
        return [];
    }

    const { date, dateRange, excludeEmpty, mergedTimeEntries } = options;
    const projectTimeMap = {};

    // Use merged entries if provided, otherwise use user.timeEntries
    const timeEntriesToUse = mergedTimeEntries || user.timeEntries;

    // Filter time entries based on options
    const filteredTimeEntries = timeEntriesToUse
        ? Object.values(timeEntriesToUse).filter((entry) => {
              if (!entry.startTime) return false;

              const start = entry.startTime;
              if (isNaN(start)) return false;

              // single-day filter
              if (date) {
                  const normalized = normalizeDateRange(date, date);
                  if (!normalized) return true;
                  return start >= normalized.start && start <= normalized.end;
              }

              // date-range filter
              if (dateRange) {
                  const normalized = normalizeDateRange(
                      dateRange.startDate,
                      dateRange.endDate
                  );
                  if (!normalized) return true;
                  return start >= normalized.start && start <= normalized.end;
              }
              // If no filtering criteria, include all entries
              return true;
          })
        : [];

    // Process filtered entries
    filteredTimeEntries.forEach((entry) => {
        const { projectId, startTime, endTime, overtime } = entry;

        if (!startTime || !endTime) {
            return;
        }

        if (isNaN(startTime) || isNaN(endTime)) {
            console.error(
                `Invalid date format for project ID ${projectId}. Start time or end time is not valid.`
            );
            return;
        }

        const timeDiff = (endTime - startTime) / (1000 * 60 * 60); // Time difference in hours

        if (!projectTimeMap[projectId]) {
            projectTimeMap[projectId] = { total: 0, overtime: 0 };
        }
        projectTimeMap[projectId].total += timeDiff;

        if (overtime === true) {
            projectTimeMap[projectId].overtime += timeDiff;
        }
    });

    // Determine which projects to include based on excludeEmpty flag
    // If excludeEmpty is false, include all projects (even those with no entries)
    // If excludeEmpty is true, only include projects with time entries
    const projectsToProcess = excludeEmpty === false
        ? projects // Include all projects when excludeEmpty is false
        : projects.filter(
              (project) => projectTimeMap[project.projectId] && projectTimeMap[project.projectId].total > 0
          ); // Only projects with entries when excludeEmpty is true (or undefined)

    // Separate assigned and unassigned projects for sorting
    const assignedProjects = projectsToProcess.filter(
        (project) => user.projects && user.projects[project.projectId]
    );
    const unassignedProjects = projectsToProcess.filter(
        (project) => !user.projects || !user.projects[project.projectId]
    );

    // Sort: assigned projects first (by user.projects order), then unassigned alphabetically
    const orderedProjects = [
        ...assignedProjects.sort((a, b) => {
            const orderA = user.projects[a.projectId]?.order || 0;
            const orderB = user.projects[b.projectId]?.order || 0;
            return orderA - orderB;
        }),
        ...unassignedProjects.sort((a, b) => a.name.localeCompare(b.name))
    ];

    // Merge projectTimeMap data with ordered projects
    return orderedProjects
        .map((project) => {
            const totals = projectTimeMap[project.projectId] || {
                total: 0,
                overtime: 0,
            };

            const totalTimeWorked = formatTimeFromHours(totals.total);
            const overtimeTimeWorked = formatTimeFromHours(totals.overtime);

            // Retrieve priority from user.projects
            const priority =
                user.projects[project.projectId]?.priority || "Normal"; // Default priority

            return {
                id: project.projectId,
                projectName: project.name,
                totalTimeWorked,
                overtimeTimeWorked,
                priority,
                hasTimeEntries: totals.total > 0, // Flag to indicate if the project has time entries
                order: user.projects[project.projectId]?.order ?? 0, // Include order property from user.projects
            };
        })
        .filter((project) => !excludeEmpty || project.hasTimeEntries); // Filter projects if excludeEmpty is true
}

/**
 * Calculates the total time worked for each user on a specific project within a date range.
 * Uses RTDB time entries from the store, optionally merged with Firestore entries.
 *
 * @param {Array<Object>} users - Array of user objects with timeEntries from RTDB.
 * @param {string} projectId - The project ID to filter by.
 * @param {Object} options - Options for filtering and formatting.
 * @param {Date|Object} options.dateRange - Date range object with startDate and endDate, or single Date.
 * @param {boolean} options.excludeEmpty - Whether to exclude users with no time entries.
 * @param {Object} options.mergedTimeEntriesByUser - Optional merged time entries by user (RTDB + Firestore). If provided, uses this instead of user.timeEntries
 * @returns {Array<Object>} Array of user objects with their total hours and overtime for the project.
 */
export function calculateUsersWorkSummaryForProject(
    users,
    projectId,
    options = {}
) {
    if (!users || !projectId) {
        return [];
    }

    const { dateRange, excludeEmpty, mergedTimeEntriesByUser, includeActiveEntries = false } = options;
    const userTimeMap = {};

    // Calculate date range boundaries
    let dateStart, dateEnd;
    if (dateRange) {
        const normalized = normalizeDateRange(
            dateRange.startDate,
            dateRange.endDate
        );
        if (normalized) {
            dateStart = normalized.start;
            dateEnd = normalized.end;
        }
    }

    // Single pass: Process each user's time entries
    users.forEach((user) => {
        // Use merged entries if provided, otherwise use user.timeEntries
        const userTimeEntries = mergedTimeEntriesByUser && mergedTimeEntriesByUser[user.uid]
            ? mergedTimeEntriesByUser[user.uid]
            : user.timeEntries;

        if (!userTimeEntries) return;

        const entries = Array.isArray(userTimeEntries)
            ? userTimeEntries
            : typeof userTimeEntries === "object"
            ? Object.values(userTimeEntries)
            : [];

        let hasProjectEntry = false;
        let activeEntryForProject = null;

        entries.forEach((entry) => {
            // Filter by projectId
            if (entry.projectId !== projectId) return;

            // Mark that user has worked on this project (regardless of date range)
            hasProjectEntry = true;

            // Initialize user in map if not already present
            if (!userTimeMap[user.uid]) {
                userTimeMap[user.uid] = {
                    user: user,
                    total: 0,
                    overtime: 0,
                    activeEntryStartTime: null,
                };
            }

            // Check for active entry (no endTime) - only if includeActiveEntries is true
            if (includeActiveEntries && !entry.endTime && entry.startTime) {
                const entryStart = Number(entry.startTime);
                if (!isNaN(entryStart)) {
                    // Keep track of the most recent active entry (like pickCurrentOrLast)
                    if (!activeEntryForProject || entryStart > activeEntryForProject) {
                        activeEntryForProject = entryStart;
                    }
                }
            }

            // Process completed entries within date range
            if (entry.startTime && entry.endTime) {
                // Apply date range filter for completed entries
                if (dateStart && dateEnd) {
                    const entryEnd = Number(entry.endTime);
                    if (entryEnd < dateStart || entryEnd > dateEnd) return;
                }

                const start = Number(entry.startTime);
                const end = Number(entry.endTime);

                if (isNaN(start) || isNaN(end)) return;

                const timeDiff = (end - start) / (1000 * 60 * 60); // Time difference in hours

                userTimeMap[user.uid].total += timeDiff;

                if (entry.overtime === true) {
                    userTimeMap[user.uid].overtime += timeDiff;
                }
            }
        });

        // Store active entry if found
        if (hasProjectEntry && activeEntryForProject && userTimeMap[user.uid]) {
            userTimeMap[user.uid].activeEntryStartTime = activeEntryForProject;
        }
    });

    // Format the results
    const results = Object.values(userTimeMap)
        .map(({ user, total, overtime, activeEntryStartTime }) => {
            const totalTimeWorked = formatTimeFromHours(total);
            const overtimeTimeWorked = formatTimeFromHours(overtime);

            const userName =
                user.name && user.surname
                    ? `${user.name} ${user.surname}`
                    : user.name || user.email || "Unknown User";

            return {
                id: user.uid,
                userName,
                totalTimeWorked,
                overtimeTimeWorked,
                hasTimeEntries: total > 0 || !!activeEntryStartTime,
                activeEntryStartTime: activeEntryStartTime || null,
            };
        })
        .filter((user) => !excludeEmpty || user.hasTimeEntries);

        return results

}

/**
 * Calculates project work summary across all users for a given date range.
 * Aggregates time entries from all users in RTDB for each project.
 *
 * @param {Array<Object>} users - Array of user objects with timeEntries from RTDB.
 * @param {Array<Object>} projects - Array of project objects.
 * @param {Object} options - Options for filtering and formatting.
 * @param {Object} options.dateRange - Date range object with startDate and endDate (Date objects or UTC milliseconds).
 * @param {boolean} options.excludeEmpty - Whether to exclude projects with no time entries.
 * @param {Object} options.mergedTimeEntriesByUser - Optional merged time entries by user (RTDB + Firestore). If provided, uses this instead of user.timeEntries
 * @returns {Array<Object>} Array of project objects with aggregated hours and overtime.
 */
export function calculateProjectsWorkSummaryForAllUsers(users, projects, options = {}) {
    if (!users || !projects) {
        return [];
    }

    const { dateRange, excludeEmpty, mergedTimeEntriesByUser } = options;

    // Calculate date range boundaries
    let dateStart, dateEnd;
    if (dateRange) {
        const normalized = normalizeDateRange(
            dateRange.startDate,
            dateRange.endDate
        );
        if (normalized) {
            dateStart = normalized.start;
            dateEnd = normalized.end;
        }
    }

    // Initialize project accumulator
    const projectTimeMap = projects.reduce((acc, project) => {
        if (project.projectId !== "SYSTEM_LUNCH-BREAK") {
            acc[project.projectId] = {
                projectId: project.projectId,
                project: project.name,
                totalMs: 0,
                overtimeMs: 0,
                priority: project.priority || "Normal",
                isActive: project.isActive !== false,
            };
        }
        return acc;
    }, {});

    // Aggregate time entries from all users
    users.forEach((user) => {
        // Use merged entries if provided, otherwise use user.timeEntries
        const userTimeEntries = mergedTimeEntriesByUser && mergedTimeEntriesByUser[user.uid]
            ? mergedTimeEntriesByUser[user.uid]
            : user.timeEntries;

        if (!userTimeEntries) return;

        const entries = Array.isArray(userTimeEntries)
            ? userTimeEntries
            : typeof userTimeEntries === "object"
            ? Object.values(userTimeEntries)
            : [];

        entries.forEach((entry) => {
            if (!entry.startTime || !entry.endTime) return;

            const startMs = Number(entry.startTime);
            const endMs = Number(entry.endTime);

            if (isNaN(startMs) || isNaN(endMs)) return;

            // Filter by date range if provided
            if (dateStart && dateEnd) {
                // Check if entry's endTime falls within the range
                if (endMs < dateStart || endMs > dateEnd) return;
            }

            const projectId = entry.projectId;
            if (!projectTimeMap[projectId]) return;

            const timeDiff = endMs - startMs; // milliseconds
            projectTimeMap[projectId].totalMs += timeDiff;

            if (entry.overtime === true) {
                projectTimeMap[projectId].overtimeMs += timeDiff;
            }
        });
    });

    // Format results
    return Object.values(projectTimeMap)
        .map((data) => {
            return {
                projectId: data.projectId,
                project: data.project,
                projectName: data.project, // For compatibility
                totalHours: formatTimeFromMs(data.totalMs),
                overtimeHours: formatTimeFromMs(data.overtimeMs),
                overtime: formatTimeFromMs(data.overtimeMs), // For compatibility
                priority: data.priority,
                isActive: data.isActive !== false,
                hasTimeEntries: data.totalMs > 0,
            };
        })
        .filter((project) => !excludeEmpty || project.hasTimeEntries);
}

/**
 * Merges RTDB time entries (object map) with Firestore time entries (array).
 * RTDB entries take precedence for overlapping entries (more recent data).
 * Converts Firestore array format to object map format to match RTDB structure.
 *
 * @param {Object<string, Object>} rtdbEntries - RTDB entries as object map: {entryId: {startTime, endTime, ...}}
 * @param {Array<Object>} firestoreEntries - Firestore entries as array: [{id, startTime, endTime, ...}]
 * @returns {Object<string, Object>} Merged entries as object map
 */
export function mergeTimeEntries(rtdbEntries = {}, firestoreEntries = []) {
    // Start with RTDB entries
    const merged = { ...rtdbEntries };

    // Convert Firestore array to object map and merge
    firestoreEntries.forEach((entry) => {
        // Only add if not already in RTDB (RTDB takes precedence for duplicates)
        if (!merged[entry.id]) {
            merged[entry.id] = {
                startTime: entry.startTime,
                endTime: entry.endTime,
                projectId: entry.projectId,
                overtime: entry.overtime || false,
                description: entry.description || null,
            };
        }
    });

    return merged;
}

/**
 * Merges RTDB time entries with Firestore entries for multiple users.
 * Used for admin/team lead views that need data for all users.
 *
 * @param {Object<string, Object>} rtdbUsersEntries - Object mapping userId to their RTDB entries: {userId: {entryId: entry}}
 * @param {Object<string, Array>} firestoreUsersEntries - Object mapping userId to their Firestore entries: {userId: [{id, ...}]}
 * @returns {Object<string, Object>} Merged entries by user: {userId: {entryId: entry}}
 */
export function mergeTimeEntriesForUsers(rtdbUsersEntries = {}, firestoreUsersEntries = {}) {
    const merged = {};

    // Get all unique user IDs from both sources
    const allUserIds = new Set([
        ...Object.keys(rtdbUsersEntries),
        ...Object.keys(firestoreUsersEntries),
    ]);

    allUserIds.forEach((userId) => {
        const rtdbEntries = rtdbUsersEntries[userId] || {};
        const firestoreEntries = firestoreUsersEntries[userId] || [];
        merged[userId] = mergeTimeEntries(rtdbEntries, firestoreEntries);
    });

    return merged;
}

/**
 * Gets merged time entries for a single user, fetching from Firestore if needed.
 * This is the main orchestration function that checks if Firestore fetch is needed,
 * fetches missing entries, and merges them with RTDB data.
 *
 * @param {Object} user - User object with timeEntries from RTDB
 * @param {string} periodKey - Period key ("today", "lastMonth", "custom", etc.)
 * @param {Array<Date>|null} customDateRange - Custom date range (for "custom" period)
 * @returns {Promise<Object>} Merged time entries as object map: {entryId: {startTime, endTime, ...}}
 */
export async function getMergedTimeEntriesForUser(
    user,
    periodKey,
    customDateRange = null
) {
    if (!user || !user.uid) {
        return {};
    }

    // Check if Firestore fetch is needed
    const needsFirestore = shouldFetchFromFirestore(periodKey, customDateRange);

    if (!needsFirestore) {
        // Return RTDB entries only
        return user.timeEntries || {};
    }

    // Calculate the date range for Firestore fetch
    const dateRange = getDateRangeForPeriod(periodKey, customDateRange);

    // Get the portion of range that needs Firestore
    const firestoreRange = getFirestoreDateRange(dateRange);
    if (!firestoreRange) {
        return user.timeEntries || {};
    }

    // Fetch missing entries from Firestore (using consolidated function)
    const firestoreUsersEntries = await getTimeEntriesForUsersInRange(
        firestore,
        [user.uid],
        firestoreRange.startDate,
        firestoreRange.endDate
    );
    const firestoreEntries = firestoreUsersEntries[user.uid] || [];

    // Merge RTDB and Firestore entries
    return mergeTimeEntries(user.timeEntries || {}, firestoreEntries);
}

/**
 * Gets merged time entries for multiple users, fetching from Firestore if needed.
 * Used for admin/team lead views.
 *
 * @param {Array<Object>} users - Array of user objects with timeEntries from RTDB
 * @param {string} periodKey - Period key ("today", "lastMonth", "custom", etc.)
 * @param {Array<Date>|null} customDateRange - Custom date range (for "custom" period)
 * @returns {Promise<Object>} Merged entries by user: {userId: {entryId: entry}}
 */
export async function getMergedTimeEntriesForUsers(
    users,
    periodKey,
    customDateRange = null
) {
    if (!users || users.length === 0) {
        return {};
    }

    // Check if Firestore fetch is needed
    const needsFirestore = shouldFetchFromFirestore(periodKey, customDateRange);

    // Extract RTDB entries by user
    const rtdbUsersEntries = {};
    users.forEach((user) => {
        if (user && user.uid) {
            rtdbUsersEntries[user.uid] = user.timeEntries || {};
        }
    });

    if (!needsFirestore) {
        // Return RTDB entries only
        return rtdbUsersEntries;
    }

    // Calculate the date range for Firestore fetch
    const dateRange = getDateRangeForPeriod(periodKey, customDateRange);

    // Get the portion of range that needs Firestore
    const firestoreRange = getFirestoreDateRange(dateRange);
    if (!firestoreRange) {
        return rtdbUsersEntries;
    }

    // Fetch missing entries from Firestore for all users
    const userIds = users.map((u) => u.uid).filter(Boolean);
    const firestoreUsersEntries = await getTimeEntriesForUsersInRange(
        firestore,
        userIds,
        firestoreRange.startDate,
        firestoreRange.endDate
    );

    // Merge RTDB and Firestore entries
    return mergeTimeEntriesForUsers(rtdbUsersEntries, firestoreUsersEntries);
}

/**
 * Calculate the total time worked from table data.
 * @param {Array} dailyReportData - Array of objects containing project names and time worked.
 * @returns {string} Total time worked in "hours:minutes" format.
 */
export function calculateTotalTimeWorked(dailyReportData) {
    let totalHours = 0;
    let totalMinutes = 0;

    dailyReportData.forEach((item) => {
        // Skip "Lunch Break" projects
        if (item.projectName === "Lunch Break") {
            return;
        }
        const timeMatch = item.totalTimeWorked.match(/(\d+)h.*?(\d+)min/);

        if (timeMatch) {
            const hrs = parseInt(timeMatch[1], 10);
            const mins = parseInt(timeMatch[2], 10);

            totalHours += hrs;
            totalMinutes += mins;
        }
    });

    // Convert total minutes to hours if 60 or more
    totalHours += Math.floor(totalMinutes / 60);
    totalMinutes = totalMinutes % 60;

    return `${totalHours}h:${totalMinutes}min`;
}

/**
 * Pick the entry a user is currently working on, or if none is open,
 * pick the most recent closed entry.
 *
 * Logic:
 *  1) Return the first entry that has no endTime (the "open" entry).
 *  2) If no open entry exists, return the closed entry with the latest time
 *     (we compare by endTime first; if missing, fall back to startTime).
 *  3) If there are no entries at all, return null.
 *
 * Pick the user's current open entry (if any), otherwise the most recent closed entry.
 * Returns the entry merged with its entryId: { entryId, ...entry } or null if none.
 *
 * @param {Object<string, Object>} entriesById - Map of entryId -> entry object
 */
function pickCurrentOrLast(entriesById) {
    const entryPairs = Object.entries(entriesById || {}); // [entryId, entry]

    // 1) Prefer an open entry (one that has no endTime yet)
    const openEntryPair = entryPairs.find(
        ([, entry]) => entry && entry.endTime == null
    );
    if (openEntryPair) {
        const [openEntryId, openEntry] = openEntryPair;
        return { entryId: openEntryId, ...openEntry };
    }

    // 2) Otherwise, pick the latest closed entry.
    let latestEntryWithId = null;

    for (const [entryId, entry] of entryPairs) {
        const entryTime = entry.endTime ?? entry.startTime; // both are numbers in DB

        if (latestEntryWithId === null) {
            latestEntryWithId = { entryId, ...entry };
            continue;
        }

        const bestTimeSoFar =
            latestEntryWithId.endTime ?? latestEntryWithId.startTime;

        if (entryTime > bestTimeSoFar) {
            latestEntryWithId = { entryId, ...entry };
        }
    }

    return latestEntryWithId;
}

/**
 * Build a list of users decorated with their "current" project info.
 *
 * For each active user:
 *  - Uses pickCurrentOrLast(user.timeEntries) to decide which entry to show:
 *      * the open entry, if any,
 *      * otherwise the most recent closed entry,
 *      * otherwise null (no entries).
 *  - Maps projectId to project name.
 *  - Applies userStatus overrides:
 *      * "loggedOut" or endOfSession === true -> "End Of Day"
 *      * "outOfOffice" -> "Out Of Office"
 * Inactive users are omitted by default, unless includeInactive is true.
 *
 * @param {Array} users - Array of user objects
 * @param {Array} projects - Array of project objects
 * @param {Object} options - Options object
 * @param {boolean} options.includeInactive - If true, includes users with accountStatus !== "active"
 * @returns {Array} Array of user objects with project info
 */
export function getUsersAndProjects(users, projects, options = {}) {
    const { includeInactive = false } = options;
    
    return users
        .map((user) => {
            // When includeInactive is true, show ONLY inactive users
            // When includeInactive is false, show ONLY active users
            if (includeInactive && user.accountStatus === "active") {
                return null;
            }
            if (!includeInactive && user.accountStatus !== "active") {
                return null;
            }

            // Process users (active when includeInactive is false, inactive when includeInactive is true)
            const lastEntry = pickCurrentOrLast(user.timeEntries);
            if (lastEntry) {
                const {
                    entryId,
                    startTime,
                    endTime,
                    projectId,
                    endOfSession,
                } = lastEntry;

                let project = projects.find(
                    (p) => p.projectId === projectId
                )?.name;
                if (
                    endOfSession === true ||
                    user.userStatus === "loggedOut"
                ) {
                    project = "End Of Day";
                }
                if (user.userStatus === "outOfOffice") {
                    project = "Out Of Office";
                }

                return {
                    ...user,
                    name: user.name || user.email,
                    surname: user.surname,
                    status: user.userStatus,
                    project,
                    projectId,
                    entryId,
                    startTime,
                    endTime,
                    uid: user.uid,
                };
            }

            // If no timeEntries, still include the user (active or inactive)
            return {
                ...user,
                name: user.name || user.email,
                project: null, // No project
                startTime: null, // No start time
                endTime: null, // No end time
                uid: user.uid,
            };
        })
        .filter((user) => user !== null); // Filter out nulls
}

/**
 * Triggers the snackbar with the specified message and color.
 * @param {string} message - The message to display.
 * @param {string} color - The color of the snackbar ('success', 'error', 'info', etc.).
 */
export function triggerSnackbar(snackbar, message, color) {
    snackbar.message = message;
    snackbar.color = color;
    snackbar.show = true;
}

/**
 * Checks if the number of items in an array is less than or equal to a specified threshold.
 *
 * @param {Array} data - The array to evaluate.
 * @param {number} [threshold=10] - The maximum number of items allowed.
 * @returns {boolean} `true` if the array length is within the threshold, otherwise `false`.
 */
export function isBelowThreshold(data, threshold = 10) {
    return data.length <= threshold;
}

/**
 * Navigates to the user information page for a given user.
 * @param {Object} item - The table row item containing user data.
 * @param {Event} event - The event object triggered on row click.
 */
export function goToUserInfo(item, event) {
    navigateToUserInfo(this.$router, event?.item?.uid);
}

/**
 * Navigate to UserInfo by uid (reusable outside v-data-table row clicks).
 * @param {import('vue-router').Router} router
 * @param {string} userId
 */
export function navigateToUserInfo(router, userId) {
    if (!router || !userId) return;
    router.push({ name: "UserInfo", params: { uid: userId } });
}

/**
 * Navigate to ProjectInfo by projectId.
 * Caller should check canAccessProject when needed.
 * @param {import('vue-router').Router} router
 * @param {string} projectId
 */
export function navigateToProjectInfo(router, projectId) {
    if (!router || !projectId) return;
    router.push({ name: "ProjectInfo", params: { projectId } });
}
